// LiveKit: спикеры публикуют processed-звук (AEC/NS вкл — второй getUserMedia),
// все слышат всех до и во время записи. Активный спикер — события SDK.

import {
  ConnectionQuality, ConnectionState, DisconnectReason, LocalAudioTrack, RemoteTrack, Room, RoomEvent, Track,
  createLocalAudioTrack
} from 'livekit-client';

export { DisconnectReason };

export interface LivekitHandle {
  room: Room;
  micTrack: LocalAudioTrack | null;
  disconnect: () => void;
}

export interface LivekitCallbacks {
  onSpeakers?: (identities: string[]) => void;
  onConnectionState?: (state: 'connected' | 'connecting' | 'disconnected') => void;
  onAudioPlaybackStatus?: (playing: boolean) => void;
  onParticipantWebrtc?: (identity: string, health: WebrtcPeerHealth) => void;
  onParticipantMuted?: (identity: string, muted: boolean) => void;
  /** Только ОКОНЧАТЕЛЬНЫЙ разрыв (SDK исчерпал resume/reconnect) — здесь передозвон. */
  onDisconnected?: (reason?: DisconnectReason) => void;
}

export type WebrtcHealthState = 'healthy' | 'degraded' | 'disconnected';

export interface WebrtcPeerHealth {
  state: WebrtcHealthState;
  detail: string;
  captureState?: WebrtcHealthState | 'muted' | 'unknown';
  captureStallMs?: number;
  metrics?: WebrtcPeerMetrics;
}

export interface WebrtcPeerMetrics {
  /** RTT текущего браузера до LiveKit SFU. */
  rttMs?: number;
  jitterMs?: number;
  /** Фактическая недавняя выдержка семплов в jitter buffer. */
  bufferMs?: number;
  targetBufferMs?: number;
  lossPercent?: number;
  concealedPercent?: number;
  catchupActive?: boolean;
  acceleratedMs?: number;
}

/** На поддерживаемых мобильных браузерах помечает аудио как двусторонний звонок. */
export function setCallAudioSession(active: boolean): void {
  const audioSession = (navigator as Navigator & {
    audioSession?: { type: 'auto' | 'play-and-record' };
  }).audioSession;
  if (!audioSession) return;
  try {
    audioSession.type = active ? 'play-and-record' : 'auto';
  } catch {
    // API экспериментальный: неизвестное значение/политика браузера не должны
    // мешать обычному WebRTC-подключению.
  }
}

interface PeerProbe {
  track?: RemoteTrack;
  quality: ConnectionQuality;
  streamState: Track.StreamState;
  muted: boolean;
  emitted?: WebrtcPeerHealth;
  lastEmitAt: number;
  subscriptionFailed?: boolean;
  playoutSupported?: boolean;
  playoutValue?: number;
  lastPlayoutProgressAt: number;
  previous?: AudioStatsSnapshot;
  metrics?: WebrtcPeerMetrics;
  highBufferSince?: number;
  lastBufferResetAt?: number;
}

interface AudioStatsSnapshot {
  received: number;
  lost: number;
  jitterDelay: number;
  targetDelay: number;
  emitted: number;
  samples: number;
  concealed: number;
  removed: number;
  sampleDuration: number;
}

interface LocalCaptureProbe {
  sourceDuration?: number;
  lastProgressAt: number;
}

// ВАЖНО: у SDK 5 состояний. SignalReconnecting/Reconnecting — SDK чинит сессию САМ,
// без потери опубликованного трека; рвать соединение в этот момент нельзя (был баг:
// полный передозвон каждые ~15 с с пересозданием захвата микрофона).
function mapState(state: ConnectionState): 'connected' | 'connecting' | 'disconnected' {
  if (state === ConnectionState.Connected) return 'connected';
  if (state === ConnectionState.Disconnected) return 'disconnected';
  return 'connecting';
}

function wireRoom(room: Room, callbacks: LivekitCallbacks): void {
  const probes = new Map<string, PeerProbe>();
  const localCapture: LocalCaptureProbe = {
    lastProgressAt: Date.now()
  };
  let roomState = ConnectionState.Disconnected;

  const probeFor = (identity: string): PeerProbe => {
    let probe = probes.get(identity);
    if (!probe) {
      probe = {
        quality: ConnectionQuality.Unknown,
        streamState: Track.StreamState.Unknown,
        muted: false,
        lastEmitAt: 0,
        lastPlayoutProgressAt: Date.now()
      };
      probes.set(identity, probe);
    }
    return probe;
  };

  const emit = (identity: string, health: WebrtcPeerHealth): void => {
    const probe = probeFor(identity);
    const sameStatus = probe.emitted?.state === health.state && probe.emitted.detail === health.detail;
    // Цвет реагирует сразу, числовые stats достаточно обновлять раз в секунду.
    if (sameStatus && Date.now() - probe.lastEmitAt < 1000) return;
    probe.emitted = health;
    probe.lastEmitAt = Date.now();
    callbacks.onParticipantWebrtc?.(identity, health);
  };

  const evaluate = (identity: string, now = Date.now()): void => {
    const probe = probeFor(identity);
    const local = identity === room.localParticipant.identity;
    if (roomState === ConnectionState.Disconnected) {
      emit(identity, { state: 'disconnected', detail: 'соединение с LiveKit разорвано' });
    } else if (roomState !== ConnectionState.Connected) {
      emit(identity, { state: 'degraded', detail: 'LiveKit восстанавливает соединение' });
    } else if (probe.quality === ConnectionQuality.Lost) {
      emit(identity, { state: 'disconnected', detail: 'LiveKit не получает медиатрафик' });
    } else if (!local && probe.subscriptionFailed) {
      emit(identity, { state: 'disconnected', detail: 'не удалось подписаться на аудиотрек' });
    } else if (!local && !probe.track) {
      emit(identity, { state: 'degraded', detail: 'участник подключён, аудиотрек ещё не получен' });
    } else if (!local && !room.canPlaybackAudio) {
      emit(identity, { state: 'degraded', detail: 'браузер заблокировал воспроизведение звука' });
    } else if (probe.quality === ConnectionQuality.Poor) {
      emit(identity, { state: 'degraded', detail: 'LiveKit сообщает о плохом качестве соединения' });
    } else if (!local && probe.streamState === Track.StreamState.Paused) {
      emit(identity, { state: 'degraded', detail: 'SFU временно приостановил аудиотрек' });
    } else if (!local && probe.muted) {
      emit(identity, {
        state: 'healthy', detail: 'участник в mute, соединение LiveKit работает', metrics: probe.metrics
      });
    } else if (!local && probe.playoutSupported && now - probe.lastPlayoutProgressAt >= 2400) {
      emit(identity, {
        state: 'disconnected', detail: 'аудиовыход не воспроизводит семплы', metrics: probe.metrics
      });
    } else if (!local && probe.playoutSupported && now - probe.lastPlayoutProgressAt >= 800) {
      emit(identity, {
        state: 'degraded', detail: 'аудиовыход пропускает семплы', metrics: probe.metrics
      });
    } else {
      emit(identity, {
        state: 'healthy',
        detail: local ? 'локальный аудиопайплайн LiveKit работает' : 'приём и воспроизведение WebRTC работают',
        metrics: probe.metrics
      });
    }
  };

  const evaluateAll = (): void => {
    if (room.localParticipant.identity) evaluate(room.localParticipant.identity);
    for (const identity of room.remoteParticipants.keys()) evaluate(identity);
  };

  room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
    callbacks.onSpeakers?.(speakers.map((s) => s.identity));
  });
  room.on(RoomEvent.ConnectionStateChanged, (state) => {
    roomState = state;
    callbacks.onConnectionState?.(mapState(state));
    evaluateAll();
  });
  room.on(RoomEvent.AudioPlaybackStatusChanged, (playing) => {
    callbacks.onAudioPlaybackStatus?.(playing);
    evaluateAll();
  });
  room.on(RoomEvent.ParticipantConnected, (participant) => {
    probes.delete(participant.identity);
    evaluate(participant.identity);
  });
  room.on(RoomEvent.ParticipantDisconnected, (participant) => {
    const probe = probeFor(participant.identity);
    probe.track = undefined;
    probe.subscriptionFailed = true;
    callbacks.onParticipantMuted?.(participant.identity, false);
    emit(participant.identity, { state: 'disconnected', detail: 'участник отключился от LiveKit' });
  });
  room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
    if (track.kind !== Track.Kind.Audio) return;
    const probe = probeFor(participant.identity);
    probe.track = track;
    probe.playoutSupported = undefined;
    probe.playoutValue = undefined;
    probe.lastPlayoutProgressAt = Date.now();
    probe.previous = undefined;
    probe.metrics = undefined;
    probe.streamState = Track.StreamState.Active;
    probe.muted = publication.isMuted;
    callbacks.onParticipantMuted?.(participant.identity, publication.isMuted);
    probe.subscriptionFailed = false;
    evaluate(participant.identity);
  });
  room.on(RoomEvent.TrackUnsubscribed, (track, _publication, participant) => {
    if (track.kind !== Track.Kind.Audio) return;
    const probe = probeFor(participant.identity);
    if (probe.track === track) probe.track = undefined;
    probe.muted = false;
    emit(participant.identity, { state: 'disconnected', detail: 'аудиотрек отключён' });
  });
  room.on(RoomEvent.TrackMuted, (publication, participant) => {
    if (publication.kind !== Track.Kind.Audio) return;
    const probe = probeFor(participant.identity);
    probe.muted = true;
    probe.lastPlayoutProgressAt = Date.now();
    callbacks.onParticipantMuted?.(participant.identity, true);
    evaluate(participant.identity);
  });
  room.on(RoomEvent.TrackUnmuted, (publication, participant) => {
    if (publication.kind !== Track.Kind.Audio) return;
    const probe = probeFor(participant.identity);
    probe.muted = false;
    probe.playoutValue = undefined;
    probe.lastPlayoutProgressAt = Date.now();
    callbacks.onParticipantMuted?.(participant.identity, false);
    evaluate(participant.identity);
  });
  room.on(RoomEvent.TrackSubscriptionFailed, (_trackSid, participant) => {
    const probe = probeFor(participant.identity);
    probe.subscriptionFailed = true;
    evaluate(participant.identity);
  });
  room.on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
    probeFor(participant.identity).quality = quality;
    evaluate(participant.identity);
  });
  room.on(RoomEvent.TrackStreamStateChanged, (publication, streamState, participant) => {
    if (publication.kind !== Track.Kind.Audio) return;
    probeFor(participant.identity).streamState = streamState;
    evaluate(participant.identity);
  });
  room.on(RoomEvent.Disconnected, (reason) => {
    roomState = ConnectionState.Disconnected;
    evaluateAll();
    callbacks.onDisconnected?.(reason);
  });

  const finite = (value: unknown, fallback = 0): number =>
    typeof value === 'number' && Number.isFinite(value) ? value : fallback;

  const pollRemote = async (identity: string, probe: PeerProbe, now: number): Promise<void> => {
    if (!probe.track) return;
    const report = await probe.track.getRTCStatsReport();
    if (!report) return;
    let inbound: RTCInboundRtpStreamStats | undefined;
    let rttSeconds: number | undefined;
    let playoutValue: number | undefined;
    report.forEach((stat) => {
      const common = stat as RTCStats & { kind?: string; mediaType?: string };
      if (common.type === 'inbound-rtp' && (common.kind === 'audio' || common.mediaType === 'audio')) {
        const candidate = stat as RTCInboundRtpStreamStats;
        if (!inbound || finite(candidate.packetsReceived) > finite(inbound.packetsReceived)) inbound = candidate;
      } else if (common.type === 'candidate-pair') {
        const pair = stat as RTCIceCandidatePairStats;
        if (pair.state === 'succeeded' && (pair.nominated || rttSeconds === undefined)) {
          rttSeconds = pair.currentRoundTripTime;
        }
      } else if (common.type === 'media-playout') {
        const playout = stat as RTCStats & { kind?: string; totalSamplesCount?: number; totalSamplesDuration?: number };
        if (!playout.kind || playout.kind === 'audio') {
          playoutValue = playout.totalSamplesCount ?? playout.totalSamplesDuration;
        }
      }
    });

    if (playoutValue !== undefined) {
      probe.playoutSupported = true;
      if (probe.playoutValue === undefined || playoutValue > probe.playoutValue) {
        probe.lastPlayoutProgressAt = now;
      }
      probe.playoutValue = playoutValue;
    } else if (probe.playoutSupported === undefined) {
      probe.playoutSupported = false;
    }

    if (!inbound) return;
    const current: AudioStatsSnapshot = {
      received: finite(inbound.packetsReceived),
      lost: finite(inbound.packetsLost),
      jitterDelay: finite(inbound.jitterBufferDelay),
      targetDelay: finite(inbound.jitterBufferTargetDelay),
      emitted: finite(inbound.jitterBufferEmittedCount),
      samples: finite(inbound.totalSamplesReceived),
      concealed: finite(inbound.concealedSamples),
      removed: finite(inbound.removedSamplesForAcceleration),
      sampleDuration: finite(inbound.totalSamplesDuration)
    };
    const previous = probe.previous;
    const metrics: WebrtcPeerMetrics = {
      rttMs: rttSeconds === undefined ? probe.metrics?.rttMs : Math.round(rttSeconds * 1000),
      jitterMs: inbound.jitter === undefined ? undefined : Math.round(inbound.jitter * 1000)
    };
    if (previous) {
      const emitted = current.emitted - previous.emitted;
      const received = current.received - previous.received;
      const lost = Math.max(0, current.lost - previous.lost);
      const samples = current.samples - previous.samples;
      const sampleDuration = current.sampleDuration - previous.sampleDuration;
      if (emitted > 0) {
        metrics.bufferMs = Math.max(0, Math.round(
          ((current.jitterDelay - previous.jitterDelay) / emitted) * 1000
        ));
        metrics.targetBufferMs = Math.max(0, Math.round(
          ((current.targetDelay - previous.targetDelay) / emitted) * 1000
        ));
      }
      if (received + lost > 0) metrics.lossPercent = (lost / (received + lost)) * 100;
      if (samples > 0) metrics.concealedPercent = Math.max(
        0, ((current.concealed - previous.concealed) / samples) * 100
      );
      const removed = current.removed - previous.removed;
      if (removed > 0 && samples > 0 && sampleDuration > 0) {
        const sampleRate = samples / sampleDuration;
        metrics.acceleratedMs = Math.round((removed / sampleRate) * 1000);
      }
    }
    probe.previous = current;

    // Большой накопившийся jitter buffer просим браузер уменьшить постепенно:
    // Chromium NetEq сам делает time-stretch без изменения высоты голоса.
    const bufferMs = metrics.bufferMs ?? probe.metrics?.bufferMs;
    const receiver = probe.track.receiver;
    if (bufferMs !== undefined && bufferMs >= 300 && receiver) {
      probe.highBufferSince ??= now;
      metrics.catchupActive = true;
      try {
        if ('jitterBufferTarget' in receiver) receiver.jitterBufferTarget = 80;
        else probe.track.setPlayoutDelay(0);
      } catch { /* браузер может экспонировать, но запрещать экспериментальный setter */ }
    } else if (bufferMs !== undefined && bufferMs <= 160) {
      probe.highBufferSince = undefined;
      metrics.catchupActive = false;
      try {
        if (receiver && 'jitterBufferTarget' in receiver) receiver.jitterBufferTarget = null;
      } catch { /* возвращаем browser-controlled target, если реализация разрешает */ }
    } else if (probe.highBufferSince !== undefined) {
      metrics.catchupActive = true;
    }

    // Если мягкое догоняние не помогло за 10 с и буфер всё ещё > 0.9 с,
    // переподписка сбрасывает только этот receiver, не весь звонок.
    if (
      bufferMs !== undefined && bufferMs >= 900 && probe.highBufferSince !== undefined &&
      now - probe.highBufferSince >= 10_000 && now - (probe.lastBufferResetAt ?? 0) >= 30_000
    ) {
      probe.lastBufferResetAt = now;
      probe.highBufferSince = now;
      const publication = room.remoteParticipants.get(identity)?.getTrackPublication(Track.Source.Microphone);
      if (publication) {
        publication.setSubscribed(false);
        window.setTimeout(() => publication.setSubscribed(true), 150);
      }
    }
    probe.metrics = { ...probe.metrics, ...metrics };
  };

  const pollLocal = async (track: LocalAudioTrack, now: number): Promise<void> => {
    const report = await track.getRTCStatsReport();
    let sourceDuration: number | undefined;
    let rttSeconds: number | undefined;
    report?.forEach((stat) => {
      const common = stat as RTCStats & { kind?: string; mediaType?: string };
      if (common.type === 'media-source' && (common.kind === 'audio' || common.mediaType === 'audio')) {
        sourceDuration = (stat as RTCStats & { totalSamplesDuration?: number }).totalSamplesDuration;
      } else if (common.type === 'remote-inbound-rtp' && (common.kind === 'audio' || common.mediaType === 'audio')) {
        rttSeconds = (stat as RTCStats & { roundTripTime?: number }).roundTripTime;
      } else if (common.type === 'candidate-pair' && rttSeconds === undefined) {
        const pair = stat as RTCIceCandidatePairStats;
        if (pair.state === 'succeeded' && pair.nominated) rttSeconds = pair.currentRoundTripTime;
      }
    });
    if (sourceDuration !== undefined) {
      if (localCapture.sourceDuration === undefined || sourceDuration > localCapture.sourceDuration) {
        localCapture.lastProgressAt = now;
      }
      localCapture.sourceDuration = sourceDuration;
    }
    const mediaTrack = track.mediaStreamTrack;
    const stallMs = sourceDuration === undefined ? 0 : Math.max(0, now - localCapture.lastProgressAt);
    let state: WebrtcHealthState = 'healthy';
    let captureState: WebrtcPeerHealth['captureState'] = 'healthy';
    let detail = sourceDuration === undefined
      ? 'микрофон подключён; браузер не отдаёт media-source stats'
      : 'processed-микрофон выдаёт аудиосемплы';
    if (track.isMuted) {
      captureState = 'muted';
      detail = 'микрофон в mute, WebRTC соединён';
    } else if (mediaTrack.readyState === 'ended') {
      state = 'disconnected';
      captureState = 'disconnected';
      detail = 'браузер завершил трек микрофона';
    } else if (mediaTrack.muted || stallMs >= 2400) {
      state = 'disconnected';
      captureState = 'disconnected';
      detail = mediaTrack.muted ? 'источник микрофона перестал отдавать данные' : 'аудиосемплы микрофона не продвигаются';
    } else if (stallMs >= 800) {
      state = 'degraded';
      captureState = 'degraded';
      detail = 'processed-микрофон пропускает аудиосемплы';
    }
    emit(room.localParticipant.identity, {
      state,
      captureState,
      captureStallMs: stallMs,
      detail,
      metrics: { rttMs: rttSeconds === undefined ? undefined : Math.round(rttSeconds * 1000) }
    });
  };

  // Capture/playout stats проверяются часто, но DTX и обычная тишина больше
  // не считаются ошибкой: packet count не является heartbeat звонка.
  let statsProbeRunning = false;
  const statsProbeId = window.setInterval(() => {
    if (statsProbeRunning || roomState === ConnectionState.Disconnected) return;
    statsProbeRunning = true;
    const now = Date.now();
    const remotePolls = [...probes.entries()]
      .filter(([identity]) => identity !== room.localParticipant.identity)
      .map(async ([identity, probe]) => {
        try { await pollRemote(identity, probe, now); } catch { /* события SDK остаются fallback */ }
        evaluate(identity, now);
      });
    const localTrack = room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track;
    // Во время SignalReconnecting/Reconnecting свою карточку ведёт evaluate
    // (degraded): pollLocal смотрит только на захват и перекрасил бы её в зелёный.
    const localPoll = roomState === ConnectionState.Connected && localTrack instanceof LocalAudioTrack
      ? pollLocal(localTrack, now).catch(() => evaluate(room.localParticipant.identity, now))
      : Promise.resolve(evaluate(room.localParticipant.identity, now));
    void Promise.all([...remotePolls, localPoll]).finally(() => { statsProbeRunning = false; });
  }, 300);
  room.once(RoomEvent.Disconnected, () => window.clearInterval(statsProbeId));
}

/** Базовые констрейнты обработанного микрофона спикера (AEC/NS/AGC включены). */
export const SPEAKER_MIC_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1
} as const;

/** Подключение спикера: публикация обработанного микрофона + воспроизведение остальных.
 * existingTrack — живой захват с прошлой сессии: переиспользуем, чтобы не мигать
 * индикатором микрофона и не терять звук на пересоздании.
 * micDeviceId — выбранный микрофон; трек с другого устройства не реюзается,
 * иначе реконнект воскресил бы захват со старого микрофона. */
export async function connectAsSpeaker(
  url: string,
  token: string,
  callbacks: LivekitCallbacks,
  existingTrack?: LocalAudioTrack | null,
  micDeviceId?: string | null
): Promise<LivekitHandle> {
  setCallAudioSession(true);
  const room = new Room();
  attachPlayback(room, () => true);
  wireRoom(room, callbacks);
  await room.connect(url, token);
  callbacks.onAudioPlaybackStatus?.(room.canPlaybackAudio);
  const matchesDevice = !micDeviceId
    || existingTrack?.mediaStreamTrack.getSettings().deviceId === micDeviceId;
  const reusable = existingTrack && existingTrack.mediaStreamTrack.readyState === 'live' && matchesDevice
    ? existingTrack
    : null;
  // Отдельный захват с обработкой — независим от raw-записи (спека §3.1).
  // Исчезнувшее выбранное устройство не должно валить коннект — fallback на системный.
  const track = reusable ?? await createLocalAudioTrack(
    micDeviceId ? { ...SPEAKER_MIC_CONSTRAINTS, deviceId: { exact: micDeviceId } } : SPEAKER_MIC_CONSTRAINTS
  ).catch((error) => {
    if (!micDeviceId) throw error;
    return createLocalAudioTrack(SPEAKER_MIC_CONSTRAINTS);
  });
  // DTX оставляем включённым: обычная тишина не обязана порождать RTP. Живость
  // проверяется по media-source/playout stats, а не по количеству пакетов.
  await room.localParticipant.publishTrack(track, { dtx: true });
  return { room, micTrack: track, disconnect: () => void room.disconnect() };
}

/** Подключение слушателя (OBS-страницы): только подписка. */
export async function connectAsListener(
  url: string,
  token: string,
  callbacks: LivekitCallbacks,
  playIdentity?: (identity: string) => boolean
): Promise<LivekitHandle> {
  setCallAudioSession(true);
  const room = new Room();
  attachPlayback(room, (identity) => (playIdentity ? playIdentity(identity) : true));
  wireRoom(room, callbacks);
  await room.connect(url, token);
  callbacks.onAudioPlaybackStatus?.(room.canPlaybackAudio);
  return { room, micTrack: null, disconnect: () => void room.disconnect() };
}

function attachPlayback(room: Room, shouldPlay: (identity: string) => boolean): void {
  const elements = new Map<string, HTMLMediaElement[]>();
  room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _pub, participant) => {
    if (track.kind !== Track.Kind.Audio) return;
    if (!shouldPlay(participant.identity)) return;
    const element = track.attach();
    element.style.display = 'none';
    document.body.appendChild(element);
    const list = elements.get(participant.identity) || [];
    list.push(element);
    elements.set(participant.identity, list);
  });
  room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, _pub, participant) => {
    for (const element of track.detach()) element.remove();
    elements.delete(participant.identity);
  });
  room.on(RoomEvent.Disconnected, () => {
    for (const list of elements.values()) for (const element of list) element.remove();
    elements.clear();
  });
}

/** Chrome может блокировать автоплей до жеста — вызывать по клику. */
export async function resumePlayback(room: Room): Promise<void> {
  await room.startAudio();
}
